import { PaginationType } from '@api';
import { FileRoutesByFullPath } from '@providers';
import { UseNavigateResult } from '@tanstack/react-router';
import {
  DEFAULT_PAGE,
  getParamName,
  PAGE_SIZE_OPTIONS,
} from '../../../../common';
import { getPage, getRecordsPerPage } from './helpers';

type PaginationConfig = {
  pageSizeOptions?: number[];
};

type UseTablePaginationOptionsType = {
  navigateFn: UseNavigateResult<keyof FileRoutesByFullPath>;
  search: Record<string, unknown>;
  pagination?: PaginationType;
  config: PaginationConfig;
  tableId?: string;
};

type PaginationControllerType = {
  onPageChange: (page: number) => void;
  onRecordsPerPageChange: (recordsPerPage: number) => void;
  page: number;
  totalRecords: number;
  recordsPerPageOptions: number[];
  recordsPerPage: number;
};

export const useTablePagination = ({
  navigateFn,
  search,
  pagination,
  config: {  pageSizeOptions = PAGE_SIZE_OPTIONS },
  tableId,
}: UseTablePaginationOptionsType): PaginationControllerType => {
  const onPageChange = (page: number) => {
    navigateFn({
      search: (currentSearch) => ({
        ...currentSearch,
        [getParamName('page', tableId)]: page,
      }),
    });
  };

  const onRecordsPerPageChange = (pageSize: number) => {
    navigateFn({
      search: (currentSearch) => ({
        ...currentSearch,
        [getParamName('pageSize', tableId)]: pageSize,
        [getParamName('page', tableId)]: DEFAULT_PAGE,
      }),
    });
  };

  const page = getPage({ pagination, search, tableId });
  const recordsPerPageOptions = pageSizeOptions ?? PAGE_SIZE_OPTIONS;
  const recordsPerPage = getRecordsPerPage({
    pagination,
    search,
    tableId,
    defaultRecordsPerPage: recordsPerPageOptions[0],
  });
  const totalRecords = pagination?.total ?? 0;

  return {
    onPageChange,
    onRecordsPerPageChange,
    page,
    totalRecords,
    recordsPerPageOptions,
    recordsPerPage,
  };
};
